import { DETAIL_MAX_DAYS } from '../GscTools/period';
import { readPattern } from '../GscTools/regex';
import { emptyDraft, resolveScope } from '../GscTools/scope';
import { SCOPE_KEYS, hasScope, scopeDraftFromParams, scopeParamsFromScope } from '../GscTools/url';

/**
 * Which dimension the report's rows are keyed by. Pages and queries share one table because they
 * are read the same way; they are never mixed inside one, because a page row and a query row
 * count different things and a total across both would be meaningless.
 */
export const VIEW = { PAGES: 'pages', QUERIES: 'queries' };

/** The Search Analytics dimension each view asks for, and the one its contributors come from. */
export const VIEW_DIMENSION = { [VIEW.PAGES]: 'page', [VIEW.QUERIES]: 'query' };
export const CONTRIBUTOR_DIMENSION = { [VIEW.PAGES]: 'query', [VIEW.QUERIES]: 'page' };

/**
 * How the retrieved rows are ordered.
 *
 * Popular ranks the current period's value of the selected metric. Gaining and Declining rank the
 * signed change in that same metric and show only the rows that have one — a row returned in just
 * one period has no movement, and ordering it as if its change were zero would place it among
 * rows that genuinely did not move.
 */
export const MODE = { POPULAR: 'popular', GAINING: 'gaining', DECLINING: 'declining' };

/**
 * Local text presets over the retrieved rows.
 *
 * Neither is an API filter and neither asks Search Console anything: they test the query text the
 * response already returned. The question preset looks for visible English question words, so it
 * says nothing about queries in other languages and nothing about what Google showed for them;
 * long-tail counts whitespace-separated terms. Both only make sense against query text, so they
 * are offered on the Queries view alone.
 */
export const PRESET = { ALL: 'all', QUESTIONS: 'questions', LONG_TAIL: 'long_tail' };

/** The threshold the long-tail preset uses, displayed next to the control rather than implied. */
export const LONG_TAIL_MIN_WORDS = 4;

/** The metrics the order and the trend can be read in. */
export const ORDER_METRICS = ['clicks', 'impressions', 'ctr', 'position'];

/**
 * The dimensions a session filter rule may be written against.
 *
 * Country and device have controls of their own, so they are not offered here as well: two places
 * to filter the same dimension would send a request nobody asked for (both filters are ANDed) and
 * leave the user guessing which one won.
 */
export const RULE_DIMENSIONS = ['page', 'query'];

/**
 * The operators Search Console documents for a dimension filter. `includingRegex` and
 * `excludingRegex` take RE2, which is close to but not the same language as JavaScript's RegExp —
 * the draft is checked with RegExp as a cheap syntax gate, and a pattern that RE2 rejects comes
 * back from the API as an error rather than being caught here.
 */
export const RULE_OPERATORS = [
    'contains',
    'notContains',
    'equals',
    'notEquals',
    'includingRegex',
    'excludingRegex',
];

const REGEX_OPERATORS = new Set(['includingRegex', 'excludingRegex']);

/** The page-03 draft: the shared scope plus this report's own controls. */
export function initialDraft(period) {
    return {
        ...emptyDraft(period),
        view: VIEW.PAGES,
        mode: MODE.POPULAR,
        preset: PRESET.ALL,
        metric: 'clicks',
        // Rules, in the order they were added. Nothing is stored server side; they travel with
        // the applied scope in the page's address, so a reloaded or shared report keeps them.
        rules: [],
    };
}

/** The query-string keys this report owns on top of the shared scope ones. */
const REPORT_KEYS = ['view', 'mode', 'preset', 'metric', 'rules'];

export const URL_KEYS = [...SCOPE_KEYS, ...REPORT_KEYS];

/** Whether the address describes a report to run, counting this report's own controls too. */
export function hasReportScope(params) {
    return hasScope(params, REPORT_KEYS);
}

/**
 * One rule as the address can carry it, or null.
 *
 * A query string is typed by hand as easily as it is copied, so a rule is taken only when its
 * dimension and operator are ones Search Console is actually offered and it has something to
 * match on. Whether a regex compiles is left to `ruleError`, which reports it as a scope error
 * the user can see rather than dropping the rule silently here.
 */
function readRule(rule) {
    if (!rule || typeof rule !== 'object') return null;
    if (!RULE_DIMENSIONS.includes(rule.dimension)) return null;
    if (!RULE_OPERATORS.includes(rule.operator)) return null;
    if (typeof rule.expression !== 'string' || !rule.expression.trim()) return null;

    return {
        name: typeof rule.name === 'string' ? rule.name : '',
        dimension: rule.dimension,
        operator: rule.operator,
        expression: rule.expression,
    };
}

/** The draft this report starts from when its address already describes one. */
export function reportDraftFromParams(params) {
    const draft = scopeDraftFromParams(params);

    if (Object.values(VIEW).includes(params.view)) draft.view = params.view;
    if (Object.values(MODE).includes(params.mode)) draft.mode = params.mode;
    if (Object.values(PRESET).includes(params.preset)) draft.preset = params.preset;
    if (ORDER_METRICS.includes(params.metric)) draft.metric = params.metric;

    if (params.rules) {
        try {
            const parsed = JSON.parse(params.rules);
            const rules = (Array.isArray(parsed) ? parsed : []).map(readRule).filter(Boolean);
            if (rules.length) draft.rules = rules;
        } catch (err) {
            // A rules parameter that is not JSON is not a report anyone meant to open; the page
            // starts with no rules rather than refusing to open at all.
            console.log(err);
        }
    }

    return draft;
}

/** The applied scope as query parameters: the shared ones plus this report's own controls. */
export function reportParamsFromScope(scope) {
    return {
        ...scopeParamsFromScope(scope),
        view: scope.view,
        mode: scope.mode,
        preset: scope.preset,
        metric: scope.metric,
        rules: scope.rules?.length ? JSON.stringify(scope.rules) : undefined,
    };
}

/** An empty rule editor. `name` is a label for the chip and is never sent to Search Console. */
export function emptyRule() {
    return { name: '', dimension: 'page', operator: 'contains', expression: '' };
}

/**
 * Validates one rule the user is writing, returning the reason it cannot be added or null.
 *
 * A rule with no expression is rejected rather than being sent as an empty match, which Search
 * Console would read as a filter on the empty string instead of as no filter at all.
 */
export function ruleError(rule, t) {
    if (!rule?.expression?.trim()) return t('tools.pages_queries_changes.rules.errors.empty');

    if (REGEX_OPERATORS.has(rule.operator)) {
        try {
            // Through the shared reader, so a leading RE2 flag group such as (?i) — valid for
            // Search Console, unreadable for RegExp — is not refused here.
            readPattern(rule.expression);
        } catch (err) {
            return t('tools.pages_queries_changes.rules.errors.invalid_regex', {
                reason: err.message,
            });
        }
    }

    return null;
}

/** The rule as Search Console receives it: no name, and the expression exactly as written. */
export function ruleFilter(rule) {
    return {
        dimension: rule.dimension,
        operator: rule.operator,
        expression: rule.expression,
    };
}

/** A rule as one line of text, for the chip, the applied-scope line and the export header. */
export function ruleLabel(rule) {
    const body = `${rule.dimension} ${rule.operator} ${rule.expression}`;
    return rule.name ? `${rule.name}: ${body}` : body;
}

/**
 * The scope this report runs under.
 *
 * The shared resolver settles the property, the window and the baseline; everything added here is
 * this report's own. The period bound is the shorter detail bound rather than the headline one:
 * every request this report makes carries a page or query dimension, which multiplies the rows
 * retrieved per day, so the window it offers is the window it can actually serve — and no block
 * ever has to explain that it covers fewer days than the one above it.
 *
 * A preset that cannot apply to the current view is reported as inactive instead of being
 * silently ignored, and the rules travel as validated filters so the request builder never has to
 * second-guess them.
 */
export function resolveReportScope(draft, t) {
    const base = resolveScope(draft, t, { maxDays: DETAIL_MAX_DAYS });

    const errors = [...base.errors];
    const ruleFilters = [];

    for (const rule of draft.rules ?? []) {
        const error = ruleError(rule, t);
        if (error) errors.push(`${ruleLabel(rule)} — ${error}`);
        else ruleFilters.push(ruleFilter(rule));
    }

    const presetApplies = draft.view === VIEW.QUERIES && draft.preset !== PRESET.ALL;

    return {
        ...base,
        errors,
        view: draft.view,
        dimension: VIEW_DIMENSION[draft.view],
        contributorDimension: CONTRIBUTOR_DIMENSION[draft.view],
        mode: draft.mode,
        preset: draft.preset,
        presetApplies,
        metric: ORDER_METRICS.includes(draft.metric) ? draft.metric : 'clicks',
        rules: [...(draft.rules ?? [])],
        ruleFilters,
    };
}
